import { Box, SectionHelper, Text, TextButton, Transition } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import React, { CSSProperties, FC, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { TLocalesKeys } from '../../../../types';
import { errorsSlice } from '../../../common/reducers/errors';
import { IAttachmentError } from '../../../common/reducers/types';
import { SuccessIcon } from '../../icons';
import { TryAgainButton } from '../TryAgainButton/TryAgainButton';
import { errorMessagesDataHooks } from './ErrorMessages.dataHooks';

const listStyles: CSSProperties = {
  margin: 0,
  paddingTop: '18px',
};

const listItemStyles: CSSProperties = {
  margin: 0,
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
};

type ErrorMessageProps = Readonly<{
  error: IAttachmentError;
  show: boolean;
  index: number;
}>;

const enterAnimation = {
  fadeIn: {
    duration: 'fast02',
    easing: 'enterEasing',
  },
  moveIn: {
    direction: 'bottomToTop',
    distance: '20px',
    duration: 'fast02',
    easing: 'enterEasing',
  },
} as const;

const exitAnimation = {
  fadeOut: {
    duration: 'fast02',
    easing: 'exitEasing',
  },
  moveOut: {
    direction: 'topToBottom',
    distance: '20px',
    duration: 'fast02',
    easing: 'exitEasing',
  },
} as const;

export const ErrorMessage: FC<ErrorMessageProps> = React.memo(({ error, show, index }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const hideError = useCallback(() => {
    dispatch(errorsSlice.actions.removeError(index));
  }, [index, dispatch]);

  const icon = useMemo(() => {
    if (error.severity === 'success') {
      return (
        <Box color="G10">
          <SuccessIcon />
        </Box>
      );
    }

    return null;
  }, [error.severity]);

  const appearance = useMemo(() => {
    if (!error.severity) {
      return 'danger';
    }

    return error.severity === 'success' ? 'experimentalDark' : error.severity;
  }, [error.severity]);

  return (
    <Transition show={show} animateOnLoad enterAnimation={enterAnimation} exitAnimation={exitAnimation}>
      <SectionHelper
        appearance={appearance}
        onClose={error.isClosable === false ? undefined : hideError}
        showCloseButton={error.isClosable !== false}
        fullWidth
        size="small"
        dataHook={errorMessagesDataHooks.error({ index })}
      >
        <Box direction="vertical" gap="2">
          <Box direction="horizontal" gap="1">
            {icon && <Box direction="vertical">{icon}</Box>}
            <Box direction="vertical" verticalAlign="middle">
              <Text
                light={error.severity === 'success'}
                size="small"
                listStyle="circle"
                dataHook={errorMessagesDataHooks.errorText({ index })}
              >
                {t<TLocalesKeys>(error.localKey)}
                {error.learnMoreLink && (
                  <>
                    <br />
                    <TextButton
                      as="a"
                      href={error.learnMoreLink.href}
                      target="_blank"
                      skin="dark"
                      underline="always"
                      size="small"
                      dataHook={errorMessagesDataHooks.learnMoreTextButton({ index })}
                    >
                      {t<TLocalesKeys>(error.learnMoreLink.localKey)}
                    </TextButton>
                  </>
                )}
                {error.fileNames && (
                  // use inline styles to override default css properties
                  <ul style={listStyles} data-hook={errorMessagesDataHooks.fileNames({ index })}>
                    {error.fileNames.map((fileName, i) => (
                      <li key={i} style={listItemStyles}>
                        {fileName}
                      </li>
                    ))}
                  </ul>
                )}
              </Text>
            </Box>
          </Box>
          <TryAgainButton error={error} />
        </Box>
      </SectionHelper>
    </Transition>
  );
});
